import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import { TranslationInput } from '@/types'
import * as queries from '@/services/graphql'
import { OptionQueryKey } from '../property'
import type { PropertyDefinition } from '../property/property.server'
import { normalizePropertyTranslationLanguages } from '../property/property-field.translations'
import {
  type BlueprintCacheEntry,
  updateBlueprintCacheSections,
} from './blueprint.cache'
import {
  beginOptionCacheOperation,
  orderedOptionWrite,
  reconcileOptionCache,
  settleOptionCacheOperation,
} from '../property/option.cache'
import type { OptionItem } from '../property/property.type'
import { isRecord } from '@/utils/typeGuards'
import { normalizePropertyTranslationLanguage } from '../property/property-field.translations'

const client = getAmplifyClient()

export type UpsertTranslationVariables = {
  input: TranslationInput
  upsertType: 'create' | 'update'
  blueprintSectionsId?: string
  queryKey?: OptionQueryKey
  reconciliation?: 'background' | 'await'
}

const translationCreation = new WeakMap<QueryClient, Map<string, boolean>>()
const patchOptionTranslation =
  (input: TranslationInput) =>
  (item: OptionItem): OptionItem => {
    if (item.id !== input.optionTranslationsId) return item
    const translation = {
      id: input.id,
      label: input.label,
      lang: input.lang.toUpperCase() as import('@/types').Language,
    }
    return {
      ...item,
      translations: {
        ...item.translations,
        items: [
          ...(item.translations?.items ?? []).filter(
            (current) =>
              current.id !== input.id &&
              current.lang.toUpperCase() !== translation.lang,
          ),
          translation,
        ],
      },
    }
  }
export const useUpsertTranslation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['upsertTranslation'],
    mutationFn: (variables: UpsertTranslationVariables) => {
      if (!variables.input.optionTranslationsId)
        return upsertTranslation(variables)
      let creation = translationCreation.get(queryClient)
      if (!creation)
        translationCreation.set(queryClient, (creation = new Map()))
      if (variables.upsertType === 'create')
        creation.set(variables.input.id, false)
      const states = creation
      return orderedOptionWrite(
        queryClient,
        `translation:${variables.input.optionTranslationsId}:${variables.input.lang.toUpperCase()}`,
        async () => {
          const result = await upsertTranslation({
            ...variables,
            upsertType:
              states.get(variables.input.id) === false
                ? 'create'
                : variables.upsertType,
          })
          states.set(variables.input.id, true)
          return result
        },
      )
    },
    onMutate: async (variables) => {
      if (variables.input.optionTranslationsId && variables.queryKey)
        return beginOptionCacheOperation(
          queryClient,
          variables.queryKey[1],
          patchOptionTranslation(variables.input),
        )
      if (variables.input.sectionTranslationsId)
        updateSectionTranslationsCache(queryClient, variables)
      else updateCacheOnTranslate(queryClient, variables)
    },
    onSettled: async (data, error, variables, operation) => {
      if (operation && variables.queryKey) {
        await settleOptionCacheOperation(
          queryClient,
          operation,
          Boolean(error),
          data
            ? patchOptionTranslation({ ...variables.input, ...data })
            : undefined,
        )
        const reconciliation = reconcileOptionCache(
          queryClient,
          variables.queryKey[1],
        )
        if (variables.reconciliation !== 'background') return reconciliation
        void reconciliation
      }
      if (variables.input.fieldTranslationsId) {
        queryClient.invalidateQueries({
          queryKey: ['properties'],
          refetchType: 'all',
        })
      }

      if (
        !variables.input.sectionTranslationsId ||
        !variables.blueprintSectionsId
      ) {
        return
      }

      queryClient.invalidateQueries({
        queryKey: ['blueprint', variables.blueprintSectionsId],
      })
      queryClient.invalidateQueries({
        queryKey: ['section', variables.input.sectionTranslationsId],
      })
      queryClient.invalidateQueries({
        queryKey: ['sections'],
      })
    },
  })
}

const upsertTranslation = async ({
  input,
  upsertType,
}: UpsertTranslationVariables) => {
  try {
    const response = await client.graphql({
      query:
        upsertType === 'create'
          ? queries.createTranslationMutation
          : queries.updateTranslationMutation,
      variables: {
        input,
      },
    })
    const result = response as {
      data?: {
        createTranslation?: TranslationInput
        updateTranslation?: TranslationInput
      }
    }
    return upsertType === 'create'
      ? result.data?.createTranslation
      : result.data?.updateTranslation
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const deleteTranslation = async (id: string) => {
  try {
    await client.graphql({
      query: queries.deleteTranslationMutation,
      variables: {
        input: {
          id,
        },
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

const updateCacheOnTranslate = (
  queryClient: QueryClient,
  variables: {
    input: TranslationInput
    upsertType?: 'create' | 'update'
    blueprintSectionsId?: string
    queryKey?: OptionQueryKey
  },
) => {
  const { input, upsertType } = variables

  if (input.fieldTranslationsId) {
    updatePropertyTranslationsCache(queryClient, {
      input,
      upsertType: upsertType ?? 'update',
    })
  }
}

export const updatePropertyTranslationsCache = (
  queryClient: QueryClient,
  variables: {
    input: TranslationInput
    upsertType: 'create' | 'update'
  },
) => {
  const { input } = variables
  const fieldId = input.fieldTranslationsId
  if (!fieldId) return
  const [normalizedLang] = normalizePropertyTranslationLanguages([input.lang])
  if (!normalizedLang) return

  const nextTranslation = {
    id: input.id,
    lang: normalizedLang,
    label: input.label,
  }

  queryClient.cancelQueries({
    queryKey: ['properties'],
  })

  const propertyQueries = queryClient.getQueriesData<PropertyDefinition>({
    queryKey: ['properties'],
  })

  propertyQueries.forEach(([queryKey, property]) => {
    if (!property) return

    let hasChanges = false

    const nextItems = property.fields.items.map((item) => {
      const field = item.field
      if (!field || field.id !== fieldId) return item

      const currentTranslations = field.translations?.items ?? []
      const nextTranslations = [
        ...currentTranslations.filter((translation) => {
          const translationLang = normalizePropertyTranslationLanguage(
            translation.lang,
          )

          return (
            translation.id !== input.id && translationLang !== normalizedLang
          )
        }),
        nextTranslation,
      ]

      hasChanges = true

      return {
        ...item,
        field: {
          ...field,
          translations: {
            ...field.translations,
            items: nextTranslations,
          },
        },
      }
    })

    if (!hasChanges) return

    queryClient.setQueryData<PropertyDefinition>(queryKey, {
      ...property,
      fields: {
        ...property.fields,
        items: nextItems,
      },
    })
  })
}

export const updateSectionTranslationsCache = (
  queryClient: QueryClient,
  variables: {
    input: TranslationInput
    upsertType: 'create' | 'update'
    blueprintSectionsId?: string
  },
) => {
  const { upsertType, input, blueprintSectionsId } = variables

  queryClient.cancelQueries({
    queryKey: ['blueprint', blueprintSectionsId],
  })

  queryClient.setQueryData(
    ['blueprint', blueprintSectionsId],
    (oldData: BlueprintCacheEntry | undefined) =>
      updateBlueprintCacheSections(oldData, (sections) =>
        sections.map((section) => {
          if (section.id !== input.sectionTranslationsId) {
            return section
          }

          const currentTranslations = section.translations?.items ?? []
          const newTranslations =
            upsertType === 'create'
              ? [
                  ...currentTranslations.filter(
                    (translation) => translation.id !== input.id,
                  ),
                  input,
                ]
              : currentTranslations.map((translation) =>
                  translation.id === input.id ? input : translation,
                )

          return {
            ...section,
            translations: {
              ...section.translations,
              items: newTranslations,
            },
          }
        }),
      ),
  )

  queryClient.setQueryData(
    ['section', input.sectionTranslationsId],
    (oldData) => {
      if (!isRecord(oldData)) return oldData

      const oldTranslations = isRecord(oldData.translations)
        ? oldData.translations
        : undefined

      const currentTranslations = Array.isArray(oldTranslations?.items)
        ? oldTranslations.items
        : []

      const newTranslations =
        upsertType === 'create'
          ? [
              ...currentTranslations.filter(
                (translation) => translation?.id !== input.id,
              ),
              input,
            ]
          : currentTranslations.map((translation) =>
              translation?.id === input.id ? input : translation,
            )

      return {
        ...oldData,
        translations: {
          ...(oldTranslations ?? {}),
          items: newTranslations,
        },
      }
    },
  )
}
